// src/pages/ModuleAdminPanel.jsx
import { useState, useRef, useCallback } from "react";
import { MODULE_CONFIG, COURSES } from "./config/moduleConfig";

const API_BASE   = process.env.REACT_APP_API_URL || "http://localhost:5000";
const ADMIN_KEY  = process.env.REACT_APP_ADMIN_KEY || "";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const apiHeaders = () => ({
  "Content-Type": "application/json",
  "x-admin-key":  ADMIN_KEY,
});

function StatusBadge({ status }) {
  const map = {
    completed:  { bg: "#dcfce7", color: "#166534", label: "Completed"  },
    processing: { bg: "#fef9c3", color: "#854d0e", label: "Processing" },
    partial:    { bg: "#ffedd5", color: "#9a3412", label: "Partial"    },
  };
  const s = map[status] || { bg: "#f3f4f6", color: "#374151", label: status };
  return (
    <span style={{
      display: "inline-block",
      background: s.bg, color: s.color,
      fontWeight: 700, fontSize: 12,
      padding: "2px 10px", borderRadius: 20,
    }}>{s.label}</span>
  );
}

function EmailBadge({ status }) {
  const map = {
    emailed:  { bg: "#dcfce7", color: "#166534", icon: "✅" },
    failed:   { bg: "#fee2e2", color: "#991b1b", icon: "❌" },
    generated:{ bg: "#e0e7ff", color: "#3730a3", icon: "⏳" },
  };
  const s = map[status] || { bg: "#f3f4f6", color: "#374151", icon: "?" };
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontWeight: 700, fontSize: 11,
      padding: "2px 8px", borderRadius: 12,
    }}>{s.icon} {status}</span>
  );
}

// ─────────────────────────────────────────────────────────────
// UPLOAD FORM
// ─────────────────────────────────────────────────────────────
function UploadForm({ onSuccess }) {
  const [course,   setCourse]   = useState("");
  const [module,   setModule]   = useState("");
  const [duration, setDuration] = useState("");
  const [file,     setFile]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState("");
  const fileRef = useRef();

  const modules = course ? MODULE_CONFIG[course] : [];

  const handleCourse = (e) => {
    setCourse(e.target.value);
    setModule("");
    setDuration("");
  };

  const handleModule = (e) => {
    const name = e.target.value;
    setModule(name);
    const found = modules.find((m) => m.name === name);
    setDuration(found ? found.duration : "");
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f && !f.name.endsWith(".csv")) {
      setError("Sirf CSV file allowed hai");
      return;
    }
    setFile(f);
    setError("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith(".csv")) {
      setFile(f);
      setError("");
    } else {
      setError("Sirf CSV file allowed hai");
    }
  };

  const handleSubmit = async () => {
    if (!course || !module || !file) {
      setError("Course, module aur CSV file required hai");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);

    const fd = new FormData();
    fd.append("course",  course);
    fd.append("module",  module);
    fd.append("duration",  duration);
    fd.append("file",    file);
console.log("courses " , course)
console.log("module " , module)
console.log("file" , file)
    try {
      const resp = await fetch(`${API_BASE}/module/admin/upload-batch`, {
        method:  "POST",
        headers: { "x-admin-key": ADMIN_KEY },
        body:    fd,
      });
      const data = await resp.json();
      if (data.success) {
        setResult(data);
        setFile(null);
        fileRef.current.value = "";
        onSuccess?.();
      } else {
        setError(data.message || "Upload failed");
      }
    } catch (e) {
      setError("Network error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={card}>
      <h2 style={sectionTitle}>
        <span style={iconCircle("#eff6ff", "#ff5421")}>📤</span>
        Upload Certificate Batch
      </h2>

      {/* Step 1: Course */}
      <div style={fieldRow}>
        <label style={labelStyle}>1. Select Course</label>
        <select style={selectStyle} value={course} onChange={handleCourse}>
          <option value="">— Choose Course —</option>
          {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Step 2: Module */}
      <div style={fieldRow}>
        <label style={labelStyle}>2. Select Module</label>
        <select
          style={{ ...selectStyle, opacity: course ? 1 : 0.5 }}
          value={module}
          onChange={handleModule}
          disabled={!course}
        >
          <option value="">— Choose Module —</option>
          {modules.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
        </select>
      </div>

      {/* Duration auto-fill */}
      {duration && (
        <div style={{
          ...fieldRow,
          background: "#eff6ff", borderRadius: 10,
          padding: "10px 16px", marginBottom: 8,
        }}>
          <span style={{ color: "#ff5421", fontWeight: 700, fontSize: 14 }}>
            ⏱ Duration Auto-filled: <strong>{duration}</strong>
          </span>
        </div>
      )}

      {/* Step 3: CSV Upload */}
      <div style={fieldRow}>
        <label style={labelStyle}>3. Upload CSV File</label>
        <div
          style={{
            border: `2px dashed ${file ? "#ff5421" : "#d1d5db"}`,
            borderRadius: 12, padding: "24px 20px",
            textAlign: "center", cursor: "pointer",
            background: file ? "#eff6ff" : "#f9fafb",
            transition: "all 0.2s",
          }}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileRef.current.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={handleFile}
          />
          {file ? (
            <>
              <div style={{ fontSize: 28, marginBottom: 6 }}>📄</div>
              <p style={{ color: "#ff5421", fontWeight: 700, margin: 0 }}>{file.name}</p>
              <p style={{ color: "#64748b", fontSize: 12, margin: "4px 0 0" }}>
                Click to change
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 32, marginBottom: 8 }}>☁️</div>
              <p style={{ color: "#374151", fontWeight: 600, margin: "0 0 4px" }}>
                CSV file drag & drop karein ya click karein
              </p>
              <p style={{ color: "#94a3b8", fontSize: 12, margin: 0 }}>
                Required columns: <strong>Name, Email, Date</strong>
              </p>
            </>
          )}
        </div>
      </div>

      {/* CSV Format hint */}
      <div style={{
        background: "#fafafa", border: "1px solid #e5e7eb",
        borderRadius: 8, padding: "10px 14px", marginBottom: 16,
        fontFamily: "monospace", fontSize: 12, color: "#374151",
      }}>
        <strong style={{ color: "#6b7280" }}>CSV Format:</strong><br />
        Name,Email,Date<br />
        Rahul Sharma,rahul@gmail.com,19-06-2026<br />
        Priya Singh,priya@gmail.com,19-06-2026
      </div>

      {error && (
        <div style={{ ...alertBox, background: "#fee2e2", color: "#991b1b", borderColor: "#fca5a5" }}>
          ❌ {error}
        </div>
      )}
      {result && (
        <div style={{ ...alertBox, background: "#dcfce7", color: "#166534", borderColor: "#86efac" }}>
          ✅ {result.message}<br />
          <small>Batch ID: <strong>{result.batchId}</strong> · Students: {result.totalStudents}</small>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={loading || !course || !module || !file}
        style={{
          ...primaryBtn,
          opacity: loading || !course || !module || !file ? 0.5 : 1,
          cursor:  loading || !course || !module || !file ? "not-allowed" : "pointer",
          width: "100%", marginTop: 8,
        }}
      >
        {loading ? "⏳ Processing..." : "🚀 Generate & Send Certificates"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BATCH LIST
// ─────────────────────────────────────────────────────────────
function BatchList() {
  const [batches,    setBatches]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [expanded,   setExpanded]   = useState(null);
  const [students,   setStudents]   = useState({});
  const [loadingStu, setLoadingStu] = useState(false);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/module/admin/batches`, { headers: apiHeaders() });
      const d = await r.json();
      if (d.success) setBatches(d.batches);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStudents = async (batchId) => {
    if (students[batchId]) {
      setExpanded((p) => (p === batchId ? null : batchId));
      return;
    }
    setLoadingStu(true);
    setExpanded(batchId);
    try {
      const r = await fetch(`${API_BASE}/module/admin/batch/${batchId}/students`, {
        headers: apiHeaders(),
      });
      const d = await r.json();
      if (d.success) {
        setStudents((p) => ({ ...p, [batchId]: d.students }));
      }
    } finally {
      setLoadingStu(false);
    }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm(`Batch ${batchId} aur sare students delete karein?`)) return;
    await fetch(`${API_BASE}/module/admin/batch/${batchId}`, {
      method: "DELETE", headers: apiHeaders(),
    });
    setBatches((p) => p.filter((b) => b.batchId !== batchId));
    setStudents((p) => { const n = { ...p }; delete n[batchId]; return n; });
    if (expanded === batchId) setExpanded(null);
  };

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={sectionTitle}>
          <span style={iconCircle("#fef3c7", "#b45309")}>📋</span>
          Certificate Batches
        </h2>
        <button onClick={fetchBatches} style={ghostBtn} disabled={loading}>
          {loading ? "Loading..." : "🔄 Refresh"}
        </button>
      </div>

      {!batches.length && !loading && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>📭</div>
          <p>Koi batch nahi — refresh karein ya upload karein</p>
        </div>
      )}

      {batches.map((b) => (
        <div key={b.batchId} style={{
          border: "1px solid #e5e7eb", borderRadius: 12,
          marginBottom: 12, overflow: "hidden",
        }}>
          {/* Batch Header */}
          <div style={{
            display: "flex", flexWrap: "wrap", alignItems: "center",
            gap: 12, padding: "14px 18px",
            background: expanded === b.batchId ? "#f5f3ff" : "#fff",
            cursor: "pointer",
          }} onClick={() => fetchStudents(b.batchId)}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <p style={{ margin: 0, fontWeight: 800, color: "#1e2736", fontSize: 15 }}>
                {b.course}
              </p>
              <p style={{ margin: "2px 0 0", color: "#ff5421", fontWeight: 600, fontSize: 13 }}>
                📚 {b.module}
              </p>
              <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 12 }}>
                ⏱ {b.duration} · 🗓 {new Date(b.createdAt).toLocaleDateString("en-IN")}
              </p>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>
                👥 {b.totalStudents} students
              </span>
              <span style={{ fontSize: 12, color: "#166534" }}>✅ {b.emailsSent || 0}</span>
              <span style={{ fontSize: 12, color: "#991b1b" }}>❌ {b.emailsFailed || 0}</span>
              <StatusBadge status={b.status} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={(e) => { e.stopPropagation(); deleteBatch(b.batchId); }}
                style={{ ...ghostBtn, color: "#dc2626", borderColor: "#fca5a5" }}
              >
                🗑 Delete
              </button>
              <span style={{ fontSize: 18 }}>{expanded === b.batchId ? "▲" : "▼"}</span>
            </div>
          </div>

          {/* Expanded Students Table */}
          {expanded === b.batchId && (
            <div style={{ padding: "0 18px 16px", background: "#fafafa" }}>
              {loadingStu ? (
                <p style={{ color: "#64748b", padding: "16px 0" }}>Loading students...</p>
              ) : students[b.batchId]?.length ? (
                <div style={{ overflowX: "auto" }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        {["#", "Name", "Email", "Date", "Verify ID", "Status"].map((h) => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {students[b.batchId].map((s, i) => (
                        <tr key={s._id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                          <td style={tdStyle}>{i + 1}</td>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{s.name}</td>
                          <td style={{ ...tdStyle, color: "#ff5421", fontSize: 12 }}>{s.email}</td>
                          <td style={tdStyle}>{s.completionDate}</td>
                          <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 11, color: "#6b7280" }}>
                            {s.verifyId}
                          </td>
                          <td style={tdStyle}><EmailBadge status={s.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: "#94a3b8", padding: "12px 0" }}>No students found</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SEARCH PANEL
// ─────────────────────────────────────────────────────────────
function SearchPanel() {
  const [email,   setEmail]   = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/module/admin/search?email=${encodeURIComponent(email)}`,
        { headers: apiHeaders() }
      );
      const d = await r.json();
      setResults(d.success ? d.results : []);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={card}>
      <h2 style={sectionTitle}>
        <span style={iconCircle("#f0fdf4", "#166534")}>🔍</span>
        Search Student
      </h2>
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <input
          style={{ ...inputStyle, flex: 1 }}
          placeholder="Student email search karein..."
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
        />
        <button onClick={search} style={primaryBtn} disabled={loading}>
          {loading ? "..." : "Search"}
        </button>
      </div>

      {results !== null && (
        results.length ? (
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {["Name", "Email", "Course", "Module", "Duration", "Date", "Status", "Verify ID"].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((s, i) => (
                  <tr key={s._id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{s.name}</td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>{s.email}</td>
                    <td style={tdStyle}>{s.course}</td>
                    <td style={{ ...tdStyle, color: "#ff5421", fontWeight: 600 }}>{s.module}</td>
                    <td style={tdStyle}>{s.duration}</td>
                    <td style={tdStyle}>{s.completionDate}</td>
                    <td style={tdStyle}><EmailBadge status={s.status} /></td>
                    <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 10, color: "#6b7280" }}>
                      {s.verifyId}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>
            ❌ No certificates found for "{email}"
          </div>
        )
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN ADMIN PANEL
// ─────────────────────────────────────────────────────────────
export default function ModuleAdminPanel() {
  const [tab, setTab] = useState("upload");
  const [refreshKey, setRefreshKey] = useState(0);

  const tabs = [
    { key: "upload", label: "📤 Upload Batch" },
    { key: "batches", label: "📋 View Batches"  },
    { key: "search",  label: "🔍 Search"        },
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)",
      fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
      padding: "24px 16px",
    }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>

        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #ff5421 0%, #7c3aed 100%)",
          borderRadius: 20, padding: "28px 36px",
          marginBottom: 28, color: "#fff",
          boxShadow: "0 8px 32px rgba(79,70,229,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 56, height: 56, background: "rgba(255,255,255,0.15)",
              borderRadius: 16, display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 28,
            }}>🎓</div>
            <div>
              <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900 }}>
                Module Certificate System
              </h1>
              <p style={{ margin: "4px 0 0", opacity: 0.8, fontSize: 14 }}>
                Uncodemy Edutech Pvt. Ltd. — Admin Panel
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: "flex", gap: 6, marginBottom: 20,
          background: "#fff", padding: 6, borderRadius: 14,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}>
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                flex: 1, padding: "10px 16px", border: "none",
                borderRadius: 10, cursor: "pointer",
                fontWeight: 700, fontSize: 13,
                fontFamily: "inherit",
                background: tab === t.key ? "#ff5421" : "transparent",
                color:      tab === t.key ? "#fff"    : "#64748b",
                transition: "all 0.2s",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Panels */}
        {tab === "upload" && (
          <UploadForm onSuccess={() => {
            setRefreshKey((k) => k + 1);
            setTab("batches");
          }} />
        )}
        {tab === "batches" && <BatchList key={refreshKey} />}
        {tab === "search"  && <SearchPanel />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SHARED STYLES
// ─────────────────────────────────────────────────────────────
const card = {
  background: "#fff",
  borderRadius: 20,
  padding: "28px 28px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
  marginBottom: 20,
};

const sectionTitle = {
  display: "flex", alignItems: "center", gap: 12,
  margin: "0 0 20px",
  fontSize: 18, fontWeight: 800, color: "#1e2736",
};

const fieldRow = { marginBottom: 16 };

const labelStyle = {
  display: "block", marginBottom: 6,
  fontWeight: 700, fontSize: 13, color: "#374151",
};

const selectStyle = {
  width: "100%", padding: "11px 14px",
  border: "1.5px solid #d1d5db", borderRadius: 10,
  fontSize: 14, color: "#1e2736",
  background: "#fff", outline: "none",
  fontFamily: "inherit", cursor: "pointer",
};

const inputStyle = {
  padding: "11px 14px",
  border: "1.5px solid #d1d5db", borderRadius: 10,
  fontSize: 14, color: "#1e2736",
  background: "#fff", outline: "none",
  fontFamily: "inherit",
};

const primaryBtn = {
  background: "linear-gradient(135deg, #ff5421, #7c3aed)",
  color: "#fff", border: "none",
  borderRadius: 10, padding: "12px 24px",
  fontWeight: 800, fontSize: 14,
  cursor: "pointer", fontFamily: "inherit",
};

const ghostBtn = {
  background: "transparent",
  border: "1.5px solid #d1d5db",
  borderRadius: 10, padding: "8px 16px",
  fontWeight: 700, fontSize: 13,
  cursor: "pointer", color: "#374151",
  fontFamily: "inherit",
};

const alertBox = {
  borderRadius: 10, border: "1px solid",
  padding: "12px 16px", marginBottom: 14,
  fontSize: 14, lineHeight: 1.6,
};

const tableStyle = {
  width: "100%", borderCollapse: "collapse",
  fontSize: 13, marginTop: 12,
};

const thStyle = {
  padding: "10px 12px", textAlign: "left",
  background: "#f1f5f9", color: "#374151",
  fontWeight: 800, fontSize: 12,
  borderBottom: "2px solid #e2e8f0",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "10px 12px", color: "#374151",
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

function iconCircle(bg, color) {
  return {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 36, height: 36, borderRadius: 10,
    background: bg, fontSize: 18, flexShrink: 0,
  };
}